import { makeAutoObservable, runInAction } from 'mobx';
import { savePreferences, loadPreferences } from '../persistence';

/** Theme mode options */
export type ThemeMode = 'system' | 'dark' | 'light';

/** Color theme options - hue overlay on top of light/dark */
export type ColorTheme = 'default' | 'ocean' | 'forest' | 'sunset' | 'royal' | 'earth';

/** Valid color themes */
const VALID_COLOR_THEMES: ColorTheme[] = ['default', 'ocean', 'forest', 'sunset', 'royal', 'earth'];

/**
 * Display settings state - theme and font scale.
 */
export class DisplayState {
  /** Theme mode: system, dark, or light */
  themeMode: ThemeMode = 'system';

  /** Color theme: hue overlay */
  colorTheme: ColorTheme = 'default';

  /** System preference for dark mode (tracked separately) */
  private systemPrefersDark = false;

  /** Font scale (1.0 = default) */
  fontScale = 1.0;

  /** Whether pericope section headings are shown in the Bible reader */
  showPericopeHeadings = true;

  /** Reading mode: word taps scroll instead of opening Strong's lookups */
  isReadingMode = false;

  constructor() {
    makeAutoObservable(this);

    if (typeof window !== 'undefined') {
      this.systemPrefersDark = window.matchMedia('(prefers-color-scheme: dark)').matches;

      window.matchMedia('(prefers-color-scheme: dark)').addEventListener('change', (e) => {
        runInAction(() => {
          this.systemPrefersDark = e.matches;
        });
      });

      this.loadFromStorage();
    }
  }

  /** Whether dark mode should be active */
  get isDarkMode(): boolean {
    if (this.themeMode === 'system') {
      return this.systemPrefersDark;
    }
    return this.themeMode === 'dark';
  }

  /** Set theme mode */
  setThemeMode(mode: ThemeMode): void {
    this.themeMode = mode;
    this.saveToStorage();
  }

  /** Set font scale (clamped to 0.5-2.0) */
  setFontScale(scale: number): void {
    this.fontScale = Math.max(0.5, Math.min(2.0, scale));
    this.saveToStorage();
  }

  /** Set pericope heading visibility */
  setShowPericopeHeadings(show: boolean): void {
    this.showPericopeHeadings = show;
    this.applyPericopeVisibility();
    this.saveToStorage();
  }

  /** Set reading mode (suppresses word-tap Strong's lookups) */
  setReadingMode(on: boolean): void {
    this.isReadingMode = on;
    this.saveToStorage();
  }

  /** Set color theme */
  setColorTheme(theme: ColorTheme): void {
    this.colorTheme = theme;
    this.applyColorTheme();
    this.saveToStorage();
  }

  /** Apply color theme to document */
  private applyColorTheme(): void {
    if (typeof document === 'undefined') return;
    const root = document.documentElement;
    // Remove all color theme classes
    VALID_COLOR_THEMES.forEach((t) => root.classList.remove(`theme-${t}`));
    // Add the current one (skip 'default' as it uses base styles)
    if (this.colorTheme !== 'default') {
      root.classList.add(`theme-${this.colorTheme}`);
    }
  }

  /** Apply pericope heading visibility to document */
  private applyPericopeVisibility(): void {
    if (typeof document === 'undefined') return;
    document.documentElement.classList.toggle('hide-pericope-headings', !this.showPericopeHeadings);
  }

  private loadFromStorage(): void {
    const prefs = loadPreferences();
    if (!prefs) return;

    if (prefs.themeMode === 'system' || prefs.themeMode === 'dark' || prefs.themeMode === 'light') {
      this.themeMode = prefs.themeMode as ThemeMode;
    } else if (typeof prefs.isDarkMode === 'boolean') {
      // Migrate from old preference format
      this.themeMode = prefs.isDarkMode ? 'dark' : 'light';
    }

    if (typeof prefs.fontScale === 'number') {
      this.fontScale = prefs.fontScale;
    }

    if (prefs.colorTheme && VALID_COLOR_THEMES.includes(prefs.colorTheme as ColorTheme)) {
      this.colorTheme = prefs.colorTheme as ColorTheme;
      this.applyColorTheme();
    }

    if (typeof prefs.showPericopeHeadings === 'boolean') {
      this.showPericopeHeadings = prefs.showPericopeHeadings;
      this.applyPericopeVisibility();
    }

    if (typeof prefs.isReadingMode === 'boolean') {
      this.isReadingMode = prefs.isReadingMode;
    }
  }

  private saveToStorage(): void {
    savePreferences({
      themeMode: this.themeMode,
      fontScale: this.fontScale,
      colorTheme: this.colorTheme,
      showPericopeHeadings: this.showPericopeHeadings,
      isReadingMode: this.isReadingMode,
    });
  }
}
